import { get } from "lodash";
import moment from "moment";
import React from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import StatusBadgeV2 from "src/components/StatusBadgeV2/StatusBadgeV2";
import DateFilter from "src/components/TableV2/DateFilter";
import FilterContainer from "src/components/TableV2/FilterContainer";
import DisTable from "src/components/TableV2/Table";
import Title from "src/components/TableV2/Title";
import { useLoading } from "src/hooks/useLoading";
import { ICommandTask } from "src/store/models/application.model";
import { IRootState } from "src/store/reducers";
import { fetchTasksList } from "src/store/reducers/tasksCommand";
import { getDateRangeForDate } from "src/util/util.dateRange";
import * as S from "../../constants/StringConstants";
import { formatDate } from "../CampaignDetail/utils";
import "./TasksTable.scss";

function mapTasks(value: ICommandTask) {
  let id;
  if (value.commentID) {
    id = `comment-${value.commentID}`;
  } else if (value.requirementID) {
    id = `requirement-${value.requirementID}`;
  } else if (value.agentID) {
    id = `agent-${value.taskID}`;
  } else {
    // should not happen
    id = "";
  }
  const priority = ["True", "1"].includes((value as any).priority);
  return {
    ...value,
    priority,
    id,
  };
}

const taskColumns = [
  { key: "name", title: S.TASK_COL_NAME, style: { minWidth: "5rem" } },
  {
    key: "dueOrFollowUpDate",
    title: S.TASK_COL_DUE,
    style: { minWidth: "10rem" },
    render: (item: ICommandTask) => formatDate(item.dueOrFollowUpDate),
    sortKey: "DueOrFollowUpDate",
  },
  { key: "type", title: S.TASK_COL_TYPE, style: { minWidth: "5rem" }, sortKey: "Type" },
  { key: "carrier", title: S.TASK_COL_CARRIER, style: { minWidth: "8rem" }, sortKey: "Carrier" },
  { key: "uwSpecialist", title: S.TASK_COL_CASE_MANAGER, style: { minWidth: "5rem" }, sortKey: "UwSpecialist" },
  { key: "agentName", title: S.TASK_COL_AGENT, style: { minWidth: "5rem" }, sortKey: "AgentName" },
  { key: "policyNumber", title: S.TASK_COL_POLICY_NUM, style: { minWidth: "12rem" }, sortKey: "PolicyNumber" },
  {
    key: "status",
    title: S.TASK_COL_STATUS,
    render: item => (item.status ? <StatusBadgeV2 status={item.status} /> : ""),
    style: { minWidth: "5rem" },
    sortKey: "Status",
  },
];

interface TasksTableProps {
  date?: Date;
  onDateChange: (date: Date) => void;
}

const TasksTable = (props: TasksTableProps) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { userID, totalTasks } = useSelector((state: IRootState) => ({
    userID: state.auth.user.userID,
    totalTasks: state.tasksCommand.total,
  }));

  const { setIsLoading } = useLoading();

  return (
    <Form
      onSubmit={() => {}}
      render={() => (
        <DisTable
          columns={taskColumns}
          initialState={{ query: { date: props.date } as any }}
          rowClassName={(data: ICommandTask) => {
            return data.priority ? "priority" : "";
          }}
          onRowClick={(data: ICommandTask) => {
            if (data.type === "Agent Task") {
              history.push(`/brokers/${data.agentID}/tasks`);
            } else {
              history.push(`/applications/${data.applicationID}`);
            }
          }}
          onUpdate={async ({ page, rows, sort, query }) => {
            setIsLoading(true);

            // update date for our app list table
            if (query?.date) {
              props.onDateChange(moment(query.date).toDate());
            }

            const result: any = await dispatch(
              fetchTasksList({
                page,
                rows,
                sort,
                query: {
                  createdById: String(userID),
                  ...getDateRangeForDate(query?.date, ["updatedFromDate", "updatedToDate"]),
                  // get all outstanding tasks
                  updatedFromDate: "1900-01-01T00:00:00Z",
                  ...query,
                },
              }),
            );
            setIsLoading(false);
            const obj = {
              total: get(result, "value.data.totalRows") as number,
              data: (get(result, "value.data.items") as ICommandTask[]).map(mapTasks),
            };
            return { ...obj };
          }}>
          <FilterContainer
            left={<Title text={S.TASK_TABLE_TITLE(totalTasks)} />}
            right={
              <div className="command-center-date-picker">
                <DateFilter fieldLabel={S.TASK_TABLE_DATE} filterKey={"date"} name={"date"} value={props.date} />
              </div>
            }
          />
        </DisTable>
      )}
    />
  );
};

export default TasksTable;
